import type { JSX } from "react";
import GoogleMap from "@/components/Map";
import { distanceB2NCordsInMeters, toastMe } from "@/utils";
import { CloseIcon, Select } from "@mantine/core";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  CopyOutlineIcon,
  DirectionOutlineIcon,
  GPSLocationOutlineIcon,
} from "@/components/Icons";
import ImagePreview from "@/components/ImagePreview";
import PopupManager from "@/components/PopupManager";
import ShareButton from "@/components/buttons/ShareButton";
import useFetch from "@/hooks/useFetch";
import { getShops } from "@/requests/page";
import { envs, mapMarkerIcon } from "@/utils/constants";
import { getS3ImageUrl } from "@/utils/routes";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "@/hooks/useCompatRouter";

export { markerYOffset } from "@/utils/constants";

export const getShareAddress = (store: StoreItem) => {
  return `${store.name}\n${store.address}\n${store.phone}\n${store.openTime}${
    store.closeDay ? " - " + store.closeDay + " Closed" : ""
  }`;
};

const getFullSizeMapEmbed = (mapEmbed?: string | null) => {
  if (!mapEmbed) return null;

  return mapEmbed
    .replace(/<iframe/i, '<iframe width="100%" height="100%"')
    .replace(/style="([^"]*)"/i, (_m, style) => {
      const normalized = style
        .replace(/\bwidth\s*:[^;]*;?/gi, "")
        .replace(/\bheight\s*:[^;]*;?/gi, "")
        .trim();
      return `style="${normalized}${normalized ? "; " : ""}width:100%; height:100%;"`;
    });
};

// store location page
const StoreLocator = () => {
  const router = useRouter();
  const [activeIndex, setActiveIndex] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [clientLocation, setClientLocation] = useState<[number, number]>([
    0, 0,
  ]);

  const [distance, setDistance] = useState(0);

  const [popupEl, setPopupEl] = useState<JSX.Element | null>(null);
  const [hideModal, setHideModal] = useState(false);

  // const [storeLocations, setStoreLocations] = useState<StoreItem[]>([]);
  const stores = useFetch(() => getShops(), {});
  const storeLocations: StoreItem[] = useMemo(
    () =>
      (stores.response || [])?.map((store) => ({
        ...store,
        lat: +(store.lat || "0") || 23.861134477486456,
        long: +(store.long || "0") || 90.3992958,
        media:
          store.media?.map((m) =>
            getS3ImageUrl(typeof m === "string" ? m : (m as any)?.path)
          ) || [],
      })),
    [stores.response]
  );

  const filteredLocations = useMemo(() => {
    return storeLocations.filter((location) => {
      let filtered = true;

      if (searchText) {
        filtered =
          location?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
          location.address?.toLowerCase().includes(searchText.toLowerCase()) ||
          location.phone?.toLowerCase().includes(searchText.toLowerCase());
      }

      if (distance > 0) {
        const distanceInMeters = distanceB2NCordsInMeters(
          clientLocation[0],
          clientLocation[1],
          location.lat,
          location.long
        );

        filtered = filtered && distanceInMeters <= distance;
      }

      return filtered;
    });
  }, [searchText, distance, clientLocation, storeLocations]);

  const mcRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const first = useRef<boolean>(true);

  useEffect(() => {
    let imgCpy: HTMLDivElement | null = null;
    const clickHandler = () => {
      setHideModal(false);
    };

    const timeout = setTimeout(
      () => {
        if (mcRef.current) {
          const mc = mcRef.current as HTMLDivElement;

          const imgs = Array.from(mc.getElementsByTagName("img"));

          const img = imgs.find((img) => img.src === mapMarkerIcon);

          if (img) {
            const parent = img.parentElement as HTMLDivElement;
            if (parent) {
              const div = document.createElement("div");
              div.className = "absolute left-0 top-0 w-8 h-10 p-4";

              parent.appendChild(modalRef.current as HTMLDivElement);
              parent.appendChild(div);

              parent.style.overflow = "visible";

              div.addEventListener("click", clickHandler);
              imgCpy = div;
            }
          }
        }

        first.current = false;
      },
      first ? 700 : 300
    );

    return () => {
      if (imgCpy) {
        imgCpy.removeEventListener("click", clickHandler);
      }
      clearTimeout(timeout);
    };
  }, [mcRef, activeIndex, storeLocations]);

  const clientLocationGranted = useMemo(
    () => clientLocation[0] !== 0 && clientLocation[1] !== 0,
    [clientLocation]
  );

  const selectedLocation = useMemo(() => {
    return filteredLocations[activeIndex] || {};
  }, [activeIndex, filteredLocations]);

  const selectedMapEmbed = useMemo(
    () =>
      getFullSizeMapEmbed(
        (filteredLocations[activeIndex] as StoreItem & { mapEmbed?: string })
          ?.mapEmbed
      ),
    [activeIndex, filteredLocations]
  );
  const showOverlayModal = Boolean(selectedMapEmbed) && !hideModal;

  const handleImagesPreview = () => {
    setPopupEl(
      <PopupManager
        onClose={() => {
          setPopupEl(null);
        }}
      >
        <ImagePreview images={selectedLocation.media || []} />
      </PopupManager>
    );
  };

  const shareText = getShareAddress(selectedLocation);

  return (
    <div className="w-full pb-16 pt-6 relative bg-primary-white">
      <div className="container">
        <div className="flex flex-wrap">
          <div className="flex-[0_0_100%] lg:flex-[0_0_32%] w-full flex flex-col border-l-2 border-t-2 border-primary-tints2">
            <h1 className="text-lg md:text-2xl pb-4 m-4 mb-1 border-b-2 border-primary-tints2">
              Store Locations
            </h1>

            <div className="w-full flex gap-2 p-4">
              <input
                type="text"
                placeholder="Search"
                className="w-full border border-gray3/10 px-2"
                value={searchText || undefined}
                onChange={(e) => setSearchText(e.target.value)}
              />

              {clientLocationGranted ? (
                <Select
                  placeholder="Distance"
                  data={[
                    { value: "100", label: "100 meters" },
                    { value: "1000", label: "1 km" },
                    { value: "10000", label: "10 km" },
                    { value: "50000", label: "50 km" },
                    { value: "100000", label: "100 km" },
                    { value: "500000", label: "500 km" },
                    { value: "1000000", label: "1000 km" },
                  ]}
                  disabled={!clientLocationGranted}
                  title={
                    clientLocationGranted
                      ? "Select distance"
                      : "Please allow location access"
                  }
                  value={distance > 0 ? distance.toString() : ""}
                  onChange={(value) => setDistance(+(value || "0"))}
                />
              ) : (
                <button
                  className="border border-gray3/10 p-2 rounded-sm"
                  onClick={() => {
                    navigator.geolocation.getCurrentPosition(
                      (position) => {
                        setClientLocation([
                          position.coords.latitude,
                          position.coords.longitude,
                        ]);
                      },
                      (err) => {
                        if (typeof err === "object" && err.code === 1)
                          alert(
                            "Location access is blocked, please allow location access"
                          );
                      }
                    );
                  }}
                >
                  <GPSLocationOutlineIcon width={18} height={18} />
                </button>
              )}
            </div>

            <div className="p-4 bg-primary-tints2 flex flex-col gap-4 h-[67vh] min-h-[350px] overflow-auto scrollbar-ui">
              {filteredLocations.map((location, i) => {
                let classNames = "";
                if (i === activeIndex)
                  classNames = " shadow-sm text-primary !border-primary/40";

                let distance = "";

                if (clientLocation) {
                  const disM = distanceB2NCordsInMeters(
                    clientLocation[0],
                    clientLocation[1],
                    location.lat,
                    location.long
                  );

                  if (disM >= 1000) {
                    distance = (disM / 1000).toFixed(2) + " km";
                  } else {
                    distance = disM.toFixed(2) + " m";
                  }
                }

                const id = "uniqueid-store-" + i;

                return (
                  <div
                    key={i}
                    className={
                      "bg-primary-white border border-transparent p-4 cursor-pointer transition-all relative" +
                      classNames
                    }
                    onClick={() => {
                      setActiveIndex(i);
                      setHideModal(false);
                    }}
                  >
                    <div className="flex gap-2 items-start pr-16">
                      <Link
                        href={`/store-locator/${location.slug}`}
                        className="lg:pointer-events-none"
                      >
                        <h1
                          className={
                            "text-md mb-2 transition-colors " +
                            (i === activeIndex ? "text-primary" : "")
                          }
                        >
                          {location.name}
                        </h1>
                      </Link>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(
                            getShareAddress(location)
                          );
                          toastMe("Copied to clipboard", "info");
                        }}
                      >
                        <CopyOutlineIcon
                          width={18}
                          height={18}
                          className={"mb-1.5"}
                        />
                      </button>
                    </div>
                    <Link
                      href={`/store-locator/${location.slug}`}
                      className="lg:pointer-events-none"
                    >
                      <p className="text-sm text-gray2 mb-1.5 leading-6">
                        {location.address}
                      </p>
                      {location.phone && (
                        <p className="text-sm text-gray2 mb-1.5">
                          Phone: {location.phone}
                        </p>
                      )}
                      <p className="text-sm text-gray2 mb-1.5">
                        {location.openTime}
                        {location.closeDay
                          ? " - " + location.closeDay + " Closed"
                          : ""}
                      </p>
                      {distance && clientLocationGranted && (
                        <p className="text-sm font-medium text-gray2 mb-1.5">
                          Distance:{" "}
                          <span
                            className={
                              activeIndex === i
                                ? "text-primary"
                                : "text-primary-dark"
                            }
                          >
                            {distance}
                          </span>
                        </p>
                      )}
                    </Link>

                    <div className="absolute top-1 right-2 p-1 hover:!text-primary">
                      <ShareButton
                        size="sm"
                        description={shareText}
                        shareUrl={
                          envs.homePageUrl + `/store-locator/${location.slug}`
                        }
                        className="text-primary hover:!text-primary-dark transition-all"
                      />
                    </div>

                    <div id={id} className="lg:hidden"></div>
                  </div>
                );
              })}

              {!filteredLocations.length && (
                <div className="w-full text-center text-gray2 p-2">
                  No store found
                </div>
              )}
            </div>
          </div>

          <div
            className="flex-[0_0_100%] lg:flex-1 w-full relative overflow-hidden"
            ref={mcRef}
          >
            {selectedMapEmbed ? (
              <div
                className="w-full h-full min-h-[420px] [&_iframe]:w-full [&_iframe]:h-full"
                dangerouslySetInnerHTML={{ __html: selectedMapEmbed }}
              />
            ) : null
            }

            <div
              className={
                showOverlayModal
                  ? "absolute left-1/2 bottom-[20%] -translate-x-1/2 z-20"
                  : "absolute bottom-[200%] opacity-0"
              }
            >
              <div
                ref={modalRef}
                className={
                  (showOverlayModal
                    ? "relative w-[min(550px,calc(100vw-2rem))] bg-white rounded-md shadow-2xl overflow-hidden transition-opacity"
                    : "absolute w-[550px] bg-white rounded-md shadow-2xl left-1/2 bottom-[120%] -translate-x-1/2 overflow-hidden transition-opacity")
                }
                style={{
                  opacity: hideModal ? 0 : 1,
                }}
              >
                <div className="relative w-full h-64 cursor-pointer">
                  <Image
                    src={
                      filteredLocations[activeIndex]?.media?.[0] ||
                      "https://static.gadgetandgear.com/tmp/store/20220618_1655544809_367980.jpeg"
                    }
                    alt="store"
                    layout="fill"
                    // objectFit="cover"
                    onClick={handleImagesPreview}
                    unoptimized={true}
                  />

                  <button
                    className="absolute top-1 right-1 p-1 rounded-full bg-white/80 shadow-md"
                    onClick={() => setHideModal(true)}
                  >
                    <CloseIcon size="16" />
                  </button>
                </div>

                <div className="p-3">
                  <h2 className="text-sm font-medium mb-1.5">
                    {selectedLocation.name}
                  </h2>
                  <p className="text-xs text-gray2 mb-1.5">
                    {selectedLocation.address}
                  </p>
                  <p className="text-xs text-gray2 mb-1.5">
                    Phone: {selectedLocation.phone}
                  </p>
                  <p className="text-xs text-gray2 mb-2.5">
                    {selectedLocation.openTime}
                    {selectedLocation.closeDay
                      ? " - " + selectedLocation.closeDay + " Closed"
                      : ""}
                  </p>
                  <div className="flex justify-between">
                    <Link
                      href={`/store-locator/${filteredLocations[activeIndex]?.slug}`}
                      className="text-sm text-white bg-primary p-2 py-1 rounded-sm transition-all"
                    >
                      More details
                    </Link>

                    <Link
                      href={`https://www.google.com/maps/dir/?api=1&destination=${selectedLocation?.lat},${selectedLocation?.long}`}
                      target="_blank"
                      className="flex gap-2 text-sm text-white bg-secondary-blue2 p-2 py-1 rounded-sm transition-all"
                    >
                      <DirectionOutlineIcon
                        className="[&_*]:fill-white"
                        width={18}
                        height={18}
                      />
                      <span>Get directions</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {popupEl}
    </div>
  );
};

export default StoreLocator;

// import GoogleMap from "@/components/Map";
// import { distanceB2NCordsInMeters, toastMe } from "@/utils";
// import { CloseIcon, Select } from "@mantine/core";
// import { useEffect, useMemo, useRef, useState } from "react";

// import {
//   CopyOutlineIcon,
//   DirectionOutlineIcon,
//   GPSLocationOutlineIcon,
// } from "@/components/Icons";
// import ImagePreview from "@/components/ImagePreview";
// import PopupManager from "@/components/PopupManager";
// import ShareButton from "@/components/buttons/ShareButton";
// import useFetch from "@/hooks/useFetch";
// import { getShops } from "@/requests/page";
// import { envs, mapMarkerIcon } from "@/utils/constants";
// import { getS3ImageUrl } from "@/utils/routes";
// import Image from "next/image";
// import Link from "next/link";
// import { useRouter } from "@/hooks/useCompatRouter";

// export const markerYOffset = 500; // meters

// export const getShareAddress = (store: StoreItem) => {
//   return `${store.name}\n${store.address}\n${store.phone}\n${store.openTime}${
//     store.closeDay ? " - " + store.closeDay + " Closed" : ""
//   }`;
// };

// // store location page
// const StoreLocator = () => {
//   const router = useRouter();
//   const [activeIndex, setActiveIndex] = useState(0);
//   const [searchText, setSearchText] = useState("");
//   const [clientLocation, setClientLocation] = useState<[number, number]>([
//     0, 0,
//   ]);

//   const [distance, setDistance] = useState(0);

//   const [popupEl, setPopupEl] = useState<JSX.Element | null>(null);
//   const [hideModal, setHideModal] = useState(false);

//   // const [storeLocations, setStoreLocations] = useState<StoreItem[]>([]);
//   const stores = useFetch(() => getShops(), {});
//   const storeLocations: StoreItem[] = useMemo(
//     () =>
//       (stores.response || [])?.map((store) => ({
//         ...store,
//         lat: +(store.lat || "0") || 23.861134477486456,
//         long: +(store.long || "0") || 90.3992958,
//         media:
//           store.media?.map((m) =>
//             getS3ImageUrl(typeof m === "string" ? m : (m as any)?.path)
//           ) || [],
//       })),
//     [stores.response]
//   );

//   const filteredLocations = useMemo(() => {
//     return storeLocations.filter((location) => {
//       let filtered = true;

//       if (searchText) {
//         filtered =
//           location?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
//           location.address?.toLowerCase().includes(searchText.toLowerCase()) ||
//           location.phone?.toLowerCase().includes(searchText.toLowerCase());
//       }

//       if (distance > 0) {
//         const distanceInMeters = distanceB2NCordsInMeters(
//           clientLocation[0],
//           clientLocation[1],
//           location.lat,
//           location.long
//         );

//         filtered = filtered && distanceInMeters <= distance;
//       }

//       return filtered;
//     });
//   }, [searchText, distance, clientLocation, storeLocations]);

//   const mcRef = useRef<HTMLDivElement>(null);
//   const modalRef = useRef<HTMLDivElement>(null);
//   const first = useRef<boolean>(true);

//   useEffect(() => {
//     let imgCpy: HTMLDivElement | null = null;
//     const clickHandler = () => {
//       setHideModal(false);
//     };

//     const timeout = setTimeout(
//       () => {
//         if (mcRef.current) {
//           const mc = mcRef.current as HTMLDivElement;

//           const imgs = Array.from(mc.getElementsByTagName("img"));

//           const img = imgs.find((img) => img.src === mapMarkerIcon);

//           if (img) {
//             const parent = img.parentElement as HTMLDivElement;
//             if (parent) {
//               const div = document.createElement("div");
//               div.className = "absolute left-0 top-0 w-8 h-10 p-4";

//               parent.appendChild(modalRef.current as HTMLDivElement);
//               parent.appendChild(div);

//               parent.style.overflow = "visible";

//               div.addEventListener("click", clickHandler);
//               imgCpy = div;
//             }
//           }
//         }

//         first.current = false;
//       },
//       first ? 700 : 300
//     );

//     return () => {
//       if (imgCpy) {
//         imgCpy.removeEventListener("click", clickHandler);
//       }
//       clearTimeout(timeout);
//     };
//   }, [mcRef, activeIndex, storeLocations]);

//   const clientLocationGranted = useMemo(
//     () => clientLocation[0] !== 0 && clientLocation[1] !== 0,
//     [clientLocation]
//   );

//   const selectedLocation = useMemo(() => {
//     return filteredLocations[activeIndex] || {};
//   }, [activeIndex, filteredLocations]);

//   const handleImagesPreview = () => {
//     setPopupEl(
//       <PopupManager
//         onClose={() => {
//           setPopupEl(null);
//         }}
//       >
//         <ImagePreview images={selectedLocation.media || []} />
//       </PopupManager>
//     );
//   };

//   const shareText = getShareAddress(selectedLocation);

//   return (
//     <div className="w-full pb-16 pt-6 relative bg-primary-white">
//       <div className="container">
//         <div className="flex flex-wrap">
//           <div className="flex-[0_0_100%] lg:flex-[0_0_32%] w-full flex flex-col border-l-2 border-t-2 border-primary-tints2">
//             <h1 className="text-lg md:text-2xl pb-4 m-4 mb-1 border-b-2 border-primary-tints2">
//               Store Locations
//             </h1>

//             <div className="w-full flex gap-2 p-4">
//               <input
//                 type="text"
//                 placeholder="Search"
//                 className="w-full border border-gray3/10 px-2"
//                 value={searchText || undefined}
//                 onChange={(e) => setSearchText(e.target.value)}
//               />

//               {clientLocationGranted ? (
//                 <Select
//                   placeholder="Distance"
//                   data={[
//                     { value: "100", label: "100 meters" },
//                     { value: "1000", label: "1 km" },
//                     { value: "10000", label: "10 km" },
//                     { value: "50000", label: "50 km" },
//                     { value: "100000", label: "100 km" },
//                     { value: "500000", label: "500 km" },
//                     { value: "1000000", label: "1000 km" },
//                   ]}
//                   disabled={!clientLocationGranted}
//                   title={
//                     clientLocationGranted
//                       ? "Select distance"
//                       : "Please allow location access"
//                   }
//                   value={distance > 0 ? distance.toString() : ""}
//                   onChange={(value) => setDistance(+(value || "0"))}
//                 />
//               ) : (
//                 <button
//                   className="border border-gray3/10 p-2 rounded-sm"
//                   onClick={() => {
//                     navigator.geolocation.getCurrentPosition(
//                       (position) => {
//                         setClientLocation([
//                           position.coords.latitude,
//                           position.coords.longitude,
//                         ]);
//                       },
//                       (err) => {
//                         if (typeof err === "object" && err.code === 1)
//                           alert(
//                             "Location access is blocked, please allow location access"
//                           );
//                       }
//                     );
//                   }}
//                 >
//                   <GPSLocationOutlineIcon width={18} height={18} />
//                 </button>
//               )}
//             </div>

//             <div className="p-4 bg-primary-tints2 flex flex-col gap-4 h-[67vh] min-h-[350px] overflow-auto scrollbar-ui">
//               {filteredLocations.map((location, i) => {
//                 let classNames = "";
//                 if (i === activeIndex)
//                   classNames = " shadow-sm text-primary !border-primary/40";

//                 let distance = "";

//                 if (clientLocation) {
//                   const disM = distanceB2NCordsInMeters(
//                     clientLocation[0],
//                     clientLocation[1],
//                     location.lat,
//                     location.long
//                   );

//                   if (disM >= 1000) {
//                     distance = (disM / 1000).toFixed(2) + " km";
//                   } else {
//                     distance = disM.toFixed(2) + " m";
//                   }
//                 }

//                 const id = "uniqueid-store-" + i;

//                 return (
//                   <div
//                     key={i}
//                     className={
//                       "bg-primary-white border border-transparent p-4 cursor-pointer transition-all relative" +
//                       classNames
//                     }
//                     onClick={() => {
//                       setActiveIndex(i);
//                       setHideModal(false);
//                     }}
//                   >
//                     <div className="flex gap-2 items-start pr-16">
//                       <Link
//                         href={`/store-locator/${location.slug}`}
//                         className="lg:pointer-events-none"
//                       >
//                         <h2
//                           className={
//                             "text-md mb-2 transition-colors " +
//                             (i === activeIndex ? "text-primary" : "")
//                           }
//                         >
//                           {location.name}
//                         </h2>
//                       </Link>
//                       <button
//                         onClick={() => {
//                           navigator.clipboard.writeText(
//                             getShareAddress(location)
//                           );
//                           toastMe("Copied to clipboard", "info");
//                         }}
//                       >
//                         <CopyOutlineIcon
//                           width={18}
//                           height={18}
//                           className={"mb-1.5"}
//                         />
//                       </button>
//                     </div>
//                     <Link
//                       href={`/store-locator/${location.slug}`}
//                       className="lg:pointer-events-none"
//                     >
//                       <p className="text-sm text-gray2 mb-1.5 leading-6">
//                         {location.address}
//                       </p>
//                       {location.phone && (
//                         <p className="text-sm text-gray2 mb-1.5">
//                           Phone: {location.phone}
//                         </p>
//                       )}
//                       <p className="text-sm text-gray2 mb-1.5">
//                         {location.openTime}
//                         {location.closeDay
//                           ? " - " + location.closeDay + " Closed"
//                           : ""}
//                       </p>

//                       {distance && clientLocationGranted && (
//                         <p className="text-sm font-medium text-gray2 mb-1.5">
//                           Distance:{" "}
//                           <span
//                             className={
//                               activeIndex === i
//                                 ? "text-primary"
//                                 : "text-primary-dark"
//                             }
//                           >
//                             {distance}
//                           </span>
//                         </p>
//                       )}
//                     </Link>

//                     <div className="absolute top-1 right-2 p-1 hover:!text-primary">
//                       <ShareButton
//                         size="sm"
//                         description={shareText}
//                         shareUrl={
//                           envs.homePageUrl + `store-locator/${location.slug}`
//                         }
//                         className="text-primary hover:!text-primary-dark transition-all"
//                       />
//                     </div>

//                     <div id={id} className="lg:hidden"></div>
//                   </div>
//                 );
//               })}

//               {!filteredLocations.length && (
//                 <div className="w-full text-center text-gray2 p-2">
//                   No store found
//                 </div>
//               )}
//             </div>
//           </div>

//           <div
//             className="flex-[0_0_100%] lg:flex-1 w-full relative overflow-hidden"
//             ref={mcRef}
//           >
//             {filteredLocations[activeIndex] && (
//               <GoogleMap
//                 streetAddress={filteredLocations[activeIndex]?.address}
//                 setClientLocation={setClientLocation}
//                 cords={[
//                   filteredLocations[activeIndex]?.lat,
//                   filteredLocations[activeIndex]?.long,
//                 ]}
//               />
//             )}

//             <div className="absolute bottom-[200%] opacity-0">
//               <div
//                 ref={modalRef}
//                 className="absolute w-[550px] bg-white rounded-md shadow-2xl left-1/2 bottom-[120%] -translate-x-1/2 overflow-hidden transition-opacity"
//                 style={{
//                   opacity: hideModal ? 0 : 1,
//                 }}
//               >
//                 <div className="relative w-full h-64 cursor-pointer">
//                   <Image
//                     src={
//                       // storeLocations[activeIndex]?.media?.[0] ||
//                       selectedLocation?.media?.[0] ||
//                       "https://static.gadgetandgear.com/tmp/store/20220618_1655544809_367980.jpeg"
//                     }
//                     alt="store"
//                     layout="fill"
//                     objectFit="cover"
//                     onClick={handleImagesPreview}
//                     unoptimized={true}
//                   />

//                   <button
//                     className="absolute top-1 right-1 p-1 rounded-full bg-white/80 shadow-md"
//                     onClick={() => setHideModal(true)}
//                   >
//                     <CloseIcon size="16" />
//                   </button>
//                 </div>

//                 <div className="p-3">
//                   <h2 className="text-sm font-medium mb-1.5">
//                     {selectedLocation.name}
//                   </h2>
//                   <p className="text-xs text-gray2 mb-1.5">
//                     {selectedLocation.address}
//                   </p>
//                   <p className="text-xs text-gray2 mb-1.5">
//                     Phone: {selectedLocation.phone}
//                   </p>
//                   <p className="text-xs text-gray2 mb-2.5">
//                     {selectedLocation.openTime}
//                     {selectedLocation.closeDay
//                       ? " - " + selectedLocation.closeDay + " Closed"
//                       : ""}
//                   </p>
//                   <div className="flex justify-between">
//                     <Link
//                       href={`/store-locator/${filteredLocations[activeIndex]?.slug}`}
//                       className="text-sm text-white bg-primary p-2 py-1 rounded-sm transition-all"
//                     >
//                       More details
//                     </Link>

//                     <Link
//                       href={`https://www.google.com/maps/dir/?api=1&destination=${selectedLocation?.lat},${selectedLocation?.long}`}
//                       target="_blank"
//                       className="flex gap-2 text-sm text-white bg-secondary-blue2 p-2 py-1 rounded-sm transition-all"
//                     >
//                       <DirectionOutlineIcon
//                         className="[&_*]:fill-white"
//                         width={18}
//                         height={18}
//                       />
//                       <span>Get directions</span>
//                     </Link>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* <LoadScript googleMapsApiKey={envs.mapApiKey}>
//                             <Map
//                                 storeEvent={activeIndex}
//                                 storeLocationData={storeLocations}
//                             />
//                         </LoadScript> */}
//           </div>
//         </div>
//       </div>

//       {popupEl}
//     </div>
//   );
// };

// export default StoreLocator;
